import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

// The SPLIT editor — "VIPs get this, everyone else gets that", without the Owl.
//
// A split forks INSTANTLY on who someone IS (ticket type, ticket category, event,
// gender, age, buyer city) rather than on what they did. The engine has run them
// since journeys shipped and the tree rendered them fine — but nothing in the
// editor could MAKE or CHANGE one, so every "different email per ticket type"
// meant opening the chat and asking for the journey to be redrafted.
//
// Values come from the client's OWN data (the same cohort-driver routes the
// segment builder uses), never a typed guess: their tiers really are called
// "VIP Weekend", and a split matching the string "VIP" would silently catch
// nobody. Mobile-first: stacked rows, ≥40px controls, no side-by-side grid.

// The everyday ones lead; the rest sit behind "More fields". Same six the
// cohort builder pins, because they are the same question asked in two places.
const PINNED = ['core_ticket_types.name', 'core_ticket_categories.name', 'core_events.name', 'core_purchasers.gender', 'core_purchasers.age', 'core_purchasers.city'];

export default function JourneySplitEditor({ entityId, node, onEdit, onBranch, suiteId = '' }) {
  const [drivers, setDrivers] = useState(null);   // [{ name, label }] | null while loading
  const [values, setValues] = useState('loading'); // real values of the CURRENT field
  const [more, setMore] = useState(false);
  const [open, setOpen] = useState(-1);            // which branch's value picker is open

  useEffect(() => {
    let alive = true;
    api.segmentCohortDrivers(entityId)
      .then((r) => { if (alive) setDrivers(r.drivers || []); })
      .catch(() => { if (alive) setDrivers([]); });
    return () => { alive = false; };
  }, [entityId]);

  const field = node.field || '';
  useEffect(() => {
    let alive = true;
    if (!field) { setValues([]); return undefined; }
    setValues('loading');
    api.segmentCohortValues(entityId, { field, suiteId })
      .then((r) => { if (alive) setValues(r.values || []); })
      .catch(() => { if (alive) setValues([]); });
    return () => { alive = false; };
  }, [entityId, field, suiteId]);

  // A split picks from REAL VALUES of the field, and the server never lists the
  // values of a date field (you don't chip-pick birthdates or purchase timestamps —
  // /cohort/values refuses them). Offering one here is a dead end: an arm with no
  // values matches everybody and swallows the arms below it. So date drivers — the
  // date of birth one included — are simply not split fields.
  const list = (drivers || []).filter((d) => d.type !== 'date');
  const pinned = PINNED.map((n) => list.find((d) => d.name === n)).filter(Boolean);
  const rest = list.filter((d) => !PINNED.includes(d.name));
  const labelFor = (name) => (list.find((d) => d.name === name) || {}).label || name;

  // Changing the FIELD clears every branch's values. They were values of the old
  // field: kept, they'd read as a live rule ("VIP") while matching a column that
  // no longer has that value — a branch that silently catches nobody.
  const pickField = (name) => {
    if (name === field) return;
    onEdit(node.id, { field: name });
    (node.branches || []).forEach((b, i) => { if (b.values !== null) onBranch(node.id, i, { values: [] }); });
    setOpen(-1);
  };

  const toggleValue = (i, v) => {
    const cur = node.branches[i].values || [];
    onBranch(node.id, i, { values: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] });
  };

  const inp = { border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'var(--text)', borderRadius: 8, padding: '7px 9px', fontSize: 12, width: '100%', boxSizing: 'border-box', minHeight: 40 };
  const chip = (on) => ({ border: `1px solid ${on ? 'var(--brand)' : 'var(--hairline)'}`, background: on ? 'var(--brand)' : 'var(--bg)', color: on ? '#fff' : 'var(--text)', borderRadius: 980, padding: '5px 11px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', minHeight: 32 });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, textAlign: 'left' }}>
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', marginBottom: 3 }}>Split on</div>
        <select style={inp} value={field} onChange={(e) => pickField(e.target.value)}>
          {!drivers && <option value={field}>Loading…</option>}
          {pinned.map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
          {(more ? rest : []).map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
          {/* A field the Owl chose that isn't in the pinned six stays selectable
              rather than silently switching to something else. */}
          {field && !pinned.some((d) => d.name === field) && !(more && rest.some((d) => d.name === field)) && <option value={field}>{labelFor(field)}</option>}
        </select>
        {!!rest.length && !more && (
          <button type="button" onClick={() => setMore(true)} style={{ border: 'none', background: 'none', color: 'var(--brand)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: '4px 0', minHeight: 30 }}>More fields…</button>
        )}
      </div>

      {(node.branches || []).map((b, i) => {
        // values === null is the catch-all arm. It takes no values by definition —
        // it is whoever none of the arms above matched.
        const isElse = b.values === null;
        const picked = b.values || [];
        return (
          <div key={i} style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: 9, background: 'var(--card)' }}>
            <input style={{ ...inp, fontWeight: 700, minHeight: 36 }} value={b.label || ''} maxLength={60}
              placeholder={isElse ? 'Everyone else' : 'Name this group'}
              onChange={(e) => onBranch(node.id, i, { label: e.target.value })} />
            {isElse ? (
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6, lineHeight: 1.45 }}>Catches everyone the arms above didn’t match. It takes no values of its own.</div>
            ) : (
              <>
                <button type="button" onClick={() => setOpen(open === i ? -1 : i)}
                  style={{ ...inp, marginTop: 6, textAlign: 'left', cursor: 'pointer', color: picked.length ? 'var(--text)' : 'var(--muted)', fontWeight: picked.length ? 700 : 500 }}>
                  {picked.length ? picked.join(', ') : `Pick which ${labelFor(field).toLowerCase()} goes down this arm`}
                </button>
                {/* An arm with no values matches EVERYONE who reaches it, and
                    branches are first-match-wins — so it swallows the ones below.
                    Say that here rather than letting the journey do it quietly. */}
                {!picked.length && <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 5, lineHeight: 1.45 }}>Nothing picked yet — as it stands everyone goes down this arm, and the arms below it never fire.</div>}
                {open === i && (
                  <div style={{ marginTop: 7, display: 'flex', flexWrap: 'wrap', gap: 5, maxHeight: 190, overflowY: 'auto' }}>
                    {values === 'loading' && <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Reading your {labelFor(field).toLowerCase()}s…</span>}
                    {values !== 'loading' && !values.length && <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>No values found for this field on your data.</span>}
                    {values !== 'loading' && values.map((v) => (
                      <button key={v} type="button" style={chip(picked.includes(v))} onClick={() => toggleValue(i, v)}>{v}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
